import React, { useEffect } from 'react';
import { getDisplayName } from '../sourceDisplayNames';
import GaugeChart from 'react-gauge-chart';

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
}

function rgbToHex(r, g, b) {
  return (
    '#' +
    [r, g, b]
      .map(x => {
        const h = x.toString(16);
        return h.length === 1 ? '0' + h : h;
      })
      .join('')
  );
}

function interpolateColor(a, b, t) {
  const ar = hexToRgb(a);
  const br = hexToRgb(b);
  const r = Math.round(ar[0] + (br[0] - ar[0]) * t);
  const g = Math.round(ar[1] + (br[1] - ar[1]) * t);
  const bVal = Math.round(ar[2] + (br[2] - ar[2]) * t);
  return rgbToHex(r, g, bVal);
}

function buildGradient(stops, count) {
  const res = [];
  const sections = stops.length - 1;
  const segPerSec = count / sections;
  for (let i = 0; i < sections; i++) {
    for (let j = 0; j < segPerSec; j++) {
      const t = j / segPerSec;
      res.push(interpolateColor(stops[i], stops[i + 1], t));
    }
  }
  return res;
}

function CVGauge({
  source,
  value = 0,
  thresholds = [0, 5, 10, 15],
  leftLabel,
  rightLabel,
  colors,
  showSideLabels = true,
  gradient = false,
  gradientStops,
}) {
  const min = thresholds[0];
  const max = thresholds[thresholds.length - 1];
  const range = max - min;

  let arcsLength = [];
  let colorSet = [];
  if (gradient) {
    const segments = 100;
    arcsLength = Array(segments).fill(1 / segments);
    const stops =
      gradientStops || ['#dc2626', '#f59e0b', '#22c55e', '#f59e0b', '#dc2626'];
    colorSet = buildGradient(stops, segments);
  } else {
    const segmentsArr = [];
    for (let i = 0; i < thresholds.length - 1; i++) {
      segmentsArr.push(thresholds[i + 1] - thresholds[i]);
    }
    arcsLength = segmentsArr.map(s => s / range);
    colorSet =
      colors ||
      (segmentsArr.length === 5
        ? ['#dc2626', '#f59e0b', '#22c55e', '#f59e0b', '#dc2626']
        : ['#dc2626', '#f59e0b', '#22c55e']);
  }

  const clamp = (num, minVal, maxVal) =>
    Math.max(Math.min(num, maxVal), minVal);
  const percent = clamp((value - min) / range, 0, 1);

  useEffect(() => {
    if (gradient) return;
    const gauge = document.getElementById(`gauge-${source}`);
    if (!gauge) return;
    const paths = gauge.querySelectorAll('path');
    paths.forEach((p, i) => {
      if (i < thresholds.length - 1) {
        const from = thresholds[i].toFixed(2);
        const to = thresholds[i + 1].toFixed(2);
        p.setAttribute('title', `${from} - ${to}`);
      }
    });
  }, [source, thresholds, gradient]);

  return (
    <div className="flex flex-col items-center w-full">
      <div className="text-xs font-semibold text-center">{getDisplayName(source)}</div>

      <div className="mt-1 w-full">
        <GaugeChart
          id={`gauge-${source}`}
          nrOfLevels={arcsLength.length}
          arcsLength={arcsLength}
          colors={colorSet}
          percent={percent}
          animate={true}
          needleColor="#ffffff"
          needleBaseColor="#ffffff"
          textColor="#0159B2"
          style={{ width: '100%', fontWeight: 'bold' }}
          arcPadding={gradient ? 0 : undefined}
          needleHeightRatio={1}
          formatTextValue={() => ''}
        />
      </div>

      {showSideLabels && (
        <div className="flex justify-between w-full px-1 mt-1">
          <span className="text-[10px] text-[#dc2626] font-semibold">
            {leftLabel !== undefined ? leftLabel : thresholds[0]}
          </span>
          <span className="text-[10px] text-[#dc2626] font-semibold">
            {rightLabel !== undefined ? rightLabel : max}
          </span>
        </div>
      )}

      <div className="mt-1">
        <span className="px-2 py-1 rounded-full bg-[#0159B2] text-white text-[13px] font-semibold">
          {value.toFixed(1)}
        </span>
      </div>
    </div>
  );
}

export default CVGauge;
